// Lightweight WebGL bevel/bubble text effect renderer (dynamic-shader independent)
// - Inputs: a sourceCanvas containing pre-rendered text (RGBA)
// - Outputs: a canvas with lighting-based bevel/bubble highlight applied using alpha-driven normals

export function renderBevelBubble(sourceCanvas, options = {}, sharedGl) {
  const width = Math.max(1, sourceCanvas.width | 0);
  const height = Math.max(1, sourceCanvas.height | 0);

  // Create WebGL canvas
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const gl =
    sharedGl ||
    canvas.getContext('webgl', { premultipliedAlpha: true }) ||
    canvas.getContext('experimental-webgl');
  if (!gl) {
    return sourceCanvas;
  }

  // Parameters with sensible defaults (balloon/glossy look)
  const {
    bevelSize = 2.0, // px normal scale; higher = rounder
    softness = 0.2, // 0..1 edge smoothing of alpha
    intensity = 1.0, // 0..1 overall effect mix
    diffuse = 0.5, // 0..1, Lambert contribution
    specular = 0.9, // 0..1, main specular
    shininess = 64.0, // spec exponent for main specular
    coat = 0.6, // 0..1, clearcoat specular weight
    coatShininess = 128.0, // spec exponent for clearcoat
    rimStrength = 0.4, // 0..1, rim highlight strength
    bulge = 2.0, // Z scale for normals; higher = more balloon-like
    shadowStrength = 0.25, // 0..1, inner shadow amount
    lightDir = [-0.4, -0.6, 0.7], // xyz, will be normalized
    highlightColor = [1.0, 1.0, 1.0],
    shadowColor = [0.0, 0.06, 0.12],
    preserveBase = 1.0, // 0..1, keep base color fully then add highlights/shadows
  } = options || {};

  // Compile shaders
  const vertSrc = `
        attribute vec2 a_position;
        attribute vec2 a_texCoord;
        varying vec2 v_uv;
        void main() {
            v_uv = a_texCoord;
            gl_Position = vec4(a_position, 0.0, 1.0);
        }
    `;

  const fragSrc = `
        precision mediump float;
        varying vec2 v_uv;
        uniform sampler2D u_src;
        uniform vec2 u_texel;           // 1.0/width, 1.0/height
        uniform float u_bevelPx;         // bevel size in pixels
        uniform float u_softness;        // 0..1
        uniform float u_intensity;       // overall intensity
        uniform float u_diffuse;         // diffuse factor
        uniform float u_specular;        // specular factor
        uniform float u_shininess;       // spec exponent
        uniform float u_coat;            // clearcoat factor
        uniform float u_coatSh;          // clearcoat shininess
        uniform float u_rim;             // rim highlight strength
        uniform float u_bulge;           // bulge Z scale
        uniform float u_shadowK;         // shadow strength
        uniform vec3 u_lightDir;         // normalized
        uniform vec3 u_highlightColor;
        uniform vec3 u_shadowColor;
        uniform float u_preserveBase;    // keep base color

        // Central differences on smoothed alpha to approximate surface normal over a wider radius
        vec2 gradient(vec2 uv) {
            vec2 dx = vec2(u_texel.x * max(1.0, u_bevelPx), 0.0);
            vec2 dy = vec2(0.0, u_texel.y * max(1.0, u_bevelPx));
            float ax = texture2D(u_src, uv + dx).a;
            float bx = texture2D(u_src, uv - dx).a;
            float ay = texture2D(u_src, uv + dy).a;
            float by = texture2D(u_src, uv - dy).a;
            return vec2(ax - bx, ay - by);
        }

        vec3 screenBlend(vec3 base, vec3 add) {
            return 1.0 - (1.0 - base) * (1.0 - add);
        }

        void main() {
            vec4 base = texture2D(u_src, v_uv);
            float a = base.a;

            // Edge smoothing
            float sm = clamp(u_softness, 0.0, 1.0);
            float mask = smoothstep(0.0, 0.4 + sm * 0.6, a);

            // Estimate normal from alpha field
            vec2 g = gradient(v_uv) * u_bevelPx; // scale by bevel size in px
            vec3 N = normalize(vec3(-g.x, -g.y, u_bulge));
            vec3 L = normalize(u_lightDir);
            vec3 V = vec3(0.0, 0.0, 1.0);
            vec3 H = normalize(L + V);

            float ndl = max(dot(N, L), 0.0);
            float diff = u_diffuse * ndl;
            float spec = 0.0;
            if (ndl > 0.0) {
                spec = pow(max(dot(N, H), 0.0), max(1.0, u_shininess)) * u_specular;
            }
            float specCoat = pow(max(dot(N, H), 0.0), max(1.0, u_coatSh)) * u_coat;
            float fres = pow(1.0 - max(dot(N, V), 0.0), 3.0) * u_rim; // rim

            // Highlight/shadow tinting
            vec3 hl = u_highlightColor * (spec + specCoat + fres);
            vec3 sh = u_shadowColor * ((1.0 - ndl) * u_shadowK);

            // Compose balloon-like shaded color
            vec3 rgb = base.rgb;
            rgb = mix(rgb, rgb * (0.85 + 0.15 * diff), 0.6);
            rgb = screenBlend(rgb, clamp(hl, 0.0, 1.0));
            rgb = mix(rgb, rgb * (1.0 - clamp(sh, 0.0, 0.6)), 0.5);

            // Apply overall intensity and preserve alpha
            rgb = mix(base.rgb, rgb, clamp(u_intensity, 0.0, 1.0));
            rgb *= mask;
            gl_FragColor = vec4(rgb, base.a);
        }
    `;

  const vert = _createShader(gl, gl.VERTEX_SHADER, vertSrc);
  const frag = _createShader(gl, gl.FRAGMENT_SHADER, fragSrc);
  if (!vert || !frag) {
    return sourceCanvas;
  }
  const program = _createProgram(gl, vert, frag);
  if (!program) {
    return sourceCanvas;
  }

  gl.useProgram(program);

  // Quad
  const posBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
    gl.STATIC_DRAW
  );

  const uvBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, uvBuffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([0, 1, 1, 1, 0, 0, 0, 0, 1, 1, 1, 0]),
    gl.STATIC_DRAW
  );

  // Attributes
  const aPos = gl.getAttribLocation(program, 'a_position');
  const aUv = gl.getAttribLocation(program, 'a_texCoord');

  gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

  gl.bindBuffer(gl.ARRAY_BUFFER, uvBuffer);
  gl.enableVertexAttribArray(aUv);
  gl.vertexAttribPointer(aUv, 2, gl.FLOAT, false, 0, 0);

  // Texture
  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, sourceCanvas);

  // Uniforms
  const uSrc = gl.getUniformLocation(program, 'u_src');
  const uTexel = gl.getUniformLocation(program, 'u_texel');
  const uBevel = gl.getUniformLocation(program, 'u_bevelPx');
  const uSoft = gl.getUniformLocation(program, 'u_softness');
  const uInt = gl.getUniformLocation(program, 'u_intensity');
  const uDif = gl.getUniformLocation(program, 'u_diffuse');
  const uSpec = gl.getUniformLocation(program, 'u_specular');
  const uSh = gl.getUniformLocation(program, 'u_shininess');
  const uCoat = gl.getUniformLocation(program, 'u_coat');
  const uCoatSh = gl.getUniformLocation(program, 'u_coatSh');
  const uRim = gl.getUniformLocation(program, 'u_rim');
  const uBulge = gl.getUniformLocation(program, 'u_bulge');
  const uShadowK = gl.getUniformLocation(program, 'u_shadowK');
  const uL = gl.getUniformLocation(program, 'u_lightDir');
  const uHl = gl.getUniformLocation(program, 'u_highlightColor');
  const uShc = gl.getUniformLocation(program, 'u_shadowColor');
  const uPreserve = gl.getUniformLocation(program, 'u_preserveBase');

  gl.uniform1i(uSrc, 0);
  gl.uniform2f(uTexel, 1 / width, 1 / height);
  gl.uniform1f(uBevel, bevelSize);
  gl.uniform1f(uSoft, Math.max(0, Math.min(1, softness)));
  gl.uniform1f(uInt, Math.max(0, Math.min(1, intensity)));
  gl.uniform1f(uDif, Math.max(0, Math.min(1, diffuse)));
  gl.uniform1f(uSpec, Math.max(0, Math.min(1, specular)));
  gl.uniform1f(uSh, Math.max(1.0, shininess));
  gl.uniform1f(uCoat, Math.max(0, Math.min(1, coat)));
  gl.uniform1f(uCoatSh, Math.max(1.0, coatShininess));
  gl.uniform1f(uRim, Math.max(0, Math.min(1, rimStrength)));
  gl.uniform1f(uBulge, Math.max(0.5, bulge));
  gl.uniform1f(uShadowK, Math.max(0.0, Math.min(1.0, shadowStrength)));
  const l = _normalize3(lightDir);
  gl.uniform3f(uL, l[0], l[1], l[2]);
  gl.uniform3f(uHl, highlightColor[0] || 0, highlightColor[1] || 0, highlightColor[2] || 0);
  gl.uniform3f(uShc, shadowColor[0] || 0, shadowColor[1] || 0, shadowColor[2] || 0);
  gl.uniform1f(uPreserve, Math.max(0, Math.min(1, preserveBase)));

  // Draw
  gl.viewport(0, 0, width, height);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.drawArrays(gl.TRIANGLES, 0, 6);

  return sharedGl ? gl.canvas : canvas;
}

function _createShader(gl, type, src) {
  const sh = gl.createShader(type);
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    console.error('TextFX shader error:', gl.getShaderInfoLog(sh));
    gl.deleteShader(sh);
    return null;
  }
  return sh;
}

function _createProgram(gl, vs, fs) {
  const p = gl.createProgram();
  gl.attachShader(p, vs);
  gl.attachShader(p, fs);
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    console.error('TextFX program link error:', gl.getProgramInfoLog(p));
    gl.deleteProgram(p);
    return null;
  }
  return p;
}

function _normalize3(v) {
  const x = Number(v && v[0]) || 0;
  const y = Number(v && v[1]) || 0;
  const z = Number(v && v[2]) || 1;
  const len = Math.sqrt(x * x + y * y + z * z) || 1;
  return [x / len, y / len, z / len];
}
